import * as React from 'react';
import { StepRunRouteComponentMatchParams, StepRunComponentProps } from '@/typescriptHelpers';
import { StepChecklistItems } from '../StepChecklistItems';
import Space from 'antd/lib/space';
import Layout from 'antd/lib/layout';
import {
    ChecklistItemPlacement,
    IntegrationCapability,
    ReleaseStepType,
    ChecklistItemStatusType,
    ChecklistItem,
    StepRunChecklistItemModel,
    RunwayResponseType,
} from '@/types';
import {
    displayTextForReleaseStepType,
    displayTextForUserGroup,
    displayTextForChecklistItemStatusType,
} from '@/helpers/displayHelpers';
import Divider from 'antd/lib/divider';
import Input from 'antd/lib/input';
import { StepRun } from '@/types/stepRun';
import { DestinationCounter } from '@/components/appHome/DestinationCounter';
import Text from 'antd/lib/typography/Text';
import { antGrey, runwayColors } from '@/components/styles/globalStyles';
import { useSelector } from 'react-redux';
import { RootState } from '@/reducers/reducers';
import {
    selectAppSettings,
    selectAppStoreType,
    selectIntegrationForCapability,
    selectAppHasSelectCapabilitiesConfigured,
    selectJoinedCommsProvidersNamesList,
    selectWorkflowRunId,
    selectWorkflowId,
    selectOrgGroups,
} from '@/selectors/selectors';
import { Link, useParams } from 'react-router-dom';
import Button from 'antd/lib/button';
import { createStepRunRoute } from '@/helpers/routeHelpers';
import { ArrowRightOutlined, CheckCircleOutlined, CheckCircleFilled } from '@ant-design/icons';
import IntegrationIcon from '@/components/icons/IntegrationIcon';
import Tooltip from 'antd/lib/tooltip';
import Popconfirm from 'antd/lib/popconfirm';
import { FilterableKeysAndOptions, FilterTableButton } from '../tableFilters/FilterTableButton';
import { useCallUpdatable, useCallUpdatableForCollections, useError, useGetResponseType } from '@/utils/hooks/helpers';
import { WorkflowRunUpdatable } from '@/actions/workflowRun';
import { globalMessage } from '@/utils/globalMessage';
import { WorkflowRunEntityRequest } from '@/actions/entities';

interface StepChecklistItemsContainerProps {
    orgId: string;
    appId: string;
    releaseId: string;
    releaseStepType: ReleaseStepType;
    stepRuns: StepRun[];
    initialItems: StepRunChecklistItemModel[];
}

const StepGroupChecklistContainer: React.FC<StepChecklistItemsContainerProps> = (
    props: StepChecklistItemsContainerProps,
) => {
    const { orgId, appId, releaseId, releaseStepType } = props;

    const platform = useSelector((state: RootState) => selectAppSettings(state, appId).platform);
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));
    const commsIntegration = useSelector((state: RootState) =>
        selectIntegrationForCapability(state, appId, IntegrationCapability.Notifications),
    );
    const hasCommsIntegrationConnected = useSelector((state: RootState) =>
        selectAppHasSelectCapabilitiesConfigured(state, appId, [IntegrationCapability.Notifications]),
    );
    const commsProviders = useSelector(selectJoinedCommsProvidersNamesList);
    const workflowRunId = useSelector((state: RootState) => selectWorkflowRunId(state, releaseId));
    const workflowId = useSelector((state: RootState) => selectWorkflowId(state, appId));
    const params = useParams<StepRunRouteComponentMatchParams>();
    const stepRunIds = props.stepRuns.map((stepRun) => stepRun.id);
    const stepArchetype = props.stepRuns[0].type;
    const stepGroupId = stepRunIds.join(',');
    const workflowRunDataByStepRunId = React.useMemo(
        () =>
            stepRunIds.reduce<Record<string, WorkflowRunEntityRequest>>((acc, stepRunId) => {
                acc[stepRunId] = {
                    orgId,
                    workflowId,
                    workflowRunId,
                };
                return acc;
            }, {}),
        [orgId, workflowId, workflowRunId, stepGroupId],
    );

    const [pingPending, isPingPendingLoading] = useCallUpdatable(
        WorkflowRunUpdatable.PingPendingChecklistItemsForStepGroup,
        {
            orgId,
            workflowId,
            workflowRunId,
        },
        stepGroupId,
    );
    const [pingPendingByStepRunId, isPingPendingLoadingByStepRunId] = useCallUpdatableForCollections(
        WorkflowRunUpdatable.PingPendingChecklistItemsForStepGroup,
        workflowRunDataByStepRunId,
        stepRunIds,
    );

    const [lastPingPendingStepRunId, setLastPingPendingStepRunId] = React.useState<string>();
    const pingPendingResponseType = useGetResponseType(
        WorkflowRunUpdatable.PingPendingChecklistItemsForStepGroup,
        lastPingPendingStepRunId,
    );
    const pingPendingError = useError(
        WorkflowRunUpdatable.PingPendingChecklistItemsForStepGroup,
        lastPingPendingStepRunId,
    );

    React.useEffect(() => {
        if (pingPendingResponseType === RunwayResponseType.Success) {
            globalMessage.success('Pinged pending cli owners');
        }
    }, [pingPendingResponseType]);

    React.useEffect(() => {
        if (pingPendingError) {
            globalMessage.error(pingPendingError);
        }
    }, [pingPendingError]);

    const onClickPingPending = (targetStepRunIds: string[], targetStepArchetype: ReleaseStepType) => {
        if (targetStepRunIds.length === 1) {
            const stepRunId = targetStepRunIds[0];
            setLastPingPendingStepRunId(stepRunId);
            pingPendingByStepRunId[stepRunId]?.({
                stepRunIds: targetStepRunIds,
                stepArchetype: targetStepArchetype,
            });
            return;
        }
        setLastPingPendingStepRunId(stepGroupId);
        pingPending({
            stepRunIds: targetStepRunIds,
            stepArchetype: targetStepArchetype,
        });
    };

    const renderPingPendingButton = (stepRunIds: string[], stepArchetype: ReleaseStepType) => {
        const loading = stepRunIds.length === 1 ? isPingPendingLoadingByStepRunId[stepRunIds[0]] : isPingPendingLoading;
        const buttonTitle = stepRunIds.length === 1 ? 'Ping pending' : 'Ping all pending';
        const button = (
            <Button
                loading={loading}
                style={{ gap: 0, marginRight: 15 }}
                icon={<IntegrationIcon integrationId={commsIntegration?.id} />}
            >
                <span style={{ paddingLeft: 4 }}>{buttonTitle}</span>
            </Button>
        );

        const buttonWithConfirmation =
            stepRunIds.length > 1 ?
                <Popconfirm
                    title="Are you sure you want to ping all owners of pending items on the step group?"
                    onConfirm={() => onClickPingPending(stepRunIds, stepArchetype)}
                    overlayStyle={{ maxWidth: 360 }}
                >
                    {button}
                </Popconfirm>
            :   <Button
                    onClick={() => onClickPingPending(stepRunIds, stepArchetype)}
                    loading={loading}
                    style={{ gap: 0, marginRight: 15 }}
                    icon={<IntegrationIcon integrationId={commsIntegration?.id} />}
                >
                    <span style={{ paddingLeft: 4 }}>{buttonTitle}</span>
                </Button>;

        if (!hasCommsIntegrationConnected) {
            return (
                <Tooltip title={`Connect ${commsProviders} to ping owners about incomplete items`}>
                    {buttonWithConfirmation}
                </Tooltip>
            );
        }

        return buttonWithConfirmation;
    };

    const [markAsCompleted, isMarkAsCompletedLoading] = useCallUpdatable(
        WorkflowRunUpdatable.BulkMarkCLIsAsCompleted,
        {
            orgId,
            workflowId,
            workflowRunId,
        },
        stepGroupId,
    );
    const [markAsCompletedByStepRunId, isMarkAsCompletedLoadingByStepRunId] = useCallUpdatableForCollections(
        WorkflowRunUpdatable.BulkMarkCLIsAsCompleted,
        workflowRunDataByStepRunId,
        stepRunIds,
    );

    const [lastMarkAsCompletedTargetId, setLastMarkAsCompletedTargetId] = React.useState<string>();
    const markAsCompletedResponseType = useGetResponseType(
        WorkflowRunUpdatable.BulkMarkCLIsAsCompleted,
        lastMarkAsCompletedTargetId,
    );
    const markAsCompletedError = useError(WorkflowRunUpdatable.BulkMarkCLIsAsCompleted, lastMarkAsCompletedTargetId);

    React.useEffect(() => {
        if (markAsCompletedResponseType === RunwayResponseType.Success) {
            globalMessage.success('Checklist items marked as completed');
        }
    }, [markAsCompletedResponseType]);

    React.useEffect(() => {
        if (markAsCompletedError) {
            globalMessage.error(markAsCompletedError);
        }
    }, [markAsCompletedError]);

    const onClickMarkAsCompleted = (targetStepRunIds: string[]) => {
        if (targetStepRunIds.length === 1) {
            const stepRunId = targetStepRunIds[0];
            setLastMarkAsCompletedTargetId(stepRunId);
            markAsCompletedByStepRunId[stepRunId]?.({
                stepRunIds: targetStepRunIds,
            });
            return;
        }
        setLastMarkAsCompletedTargetId(stepGroupId);
        markAsCompleted({
            stepRunIds: targetStepRunIds,
        });
    };

    const renderMarkAllAsCompletedButton = (stepRunIds: string[]) => {
        const loading =
            stepRunIds.length === 1 ? isMarkAsCompletedLoadingByStepRunId[stepRunIds[0]] : isMarkAsCompletedLoading;
        const buttonTitle = stepRunIds.length === 1 ? 'Mark as completed' : 'Mark all as completed';
        return stepRunIds.length > 1 ?
                <Popconfirm
                    title="Are you sure you want to mark all items on the step group as completed?"
                    onConfirm={() => onClickMarkAsCompleted(stepRunIds)}
                    overlayStyle={{ maxWidth: 360 }}
                >
                    <Button
                        loading={loading}
                        style={{ gap: 0, marginRight: 5 }}
                        icon={<CheckCircleOutlined style={{ color: runwayColors.primary, marginRight: 5 }} />}
                    >
                        {buttonTitle}
                    </Button>
                </Popconfirm>
            :   <Button
                    onClick={() => onClickMarkAsCompleted(stepRunIds)}
                    loading={loading}
                    style={{ gap: 0, marginRight: 5 }}
                    icon={<CheckCircleOutlined style={{ color: runwayColors.primary, marginRight: 5 }} />}
                >
                    {buttonTitle}
                </Button>;
    };

    const flatChecklistItems = React.useMemo<ChecklistItem[]>(() => {
        return props.stepRuns.reduce<ChecklistItem[]>((acc, stepRun) => {
            return acc.concat(stepRun.checklistItems ?? []);
        }, []);
    }, [props.stepRuns]);

    const [searchQuery, setSearchQuery] = React.useState('');
    const [filteredItems, setFilteredItems] = React.useState<StepRunChecklistItemModel[]>(props.initialItems);

    React.useEffect(() => {
        setFilteredItems(props.initialItems);
    }, [props.initialItems]);

    const userGroups = useSelector((state: RootState) => selectOrgGroups(state, orgId));

    const displayNameForGroupId = React.useMemo(() => {
        return Object.values(userGroups).reduce(
            (acc, group) => ({
                ...acc,
                [group.id]: displayTextForUserGroup(group),
            }),
            {},
        );
    }, [userGroups]);

    const searchFilteredItems = React.useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) {
            return flatChecklistItems;
        }

        return flatChecklistItems.filter((item) => {
            const searchableValues = [
                item.title,
                item.description,
                item.status?.status ? displayTextForChecklistItemStatusType(item.status.status) : '',
                ...(item.owners ?? []),
                ...(item.approverGroups ?? []).map((groupId) => displayNameForGroupId[groupId]),
            ];

            return searchableValues.some((value) => value && value.toLowerCase().includes(query));
        });
    }, [flatChecklistItems, displayNameForGroupId, searchQuery]);

    const filterableKeysAndOptions = React.useMemo(() => {
        const options: FilterableKeysAndOptions<ChecklistItem> = {
            owners: {
                displayName: 'Owners',
                displayNameForPossibleValue: (value) => {
                    return value;
                },
            },
            approverGroups: {
                displayName: 'Approver user groups',
                displayNameForPossibleValue: (value) => {
                    return displayNameForGroupId[value];
                },
                defaultValues: Object.keys(displayNameForGroupId),
                showAsDropdown: true,
            },
            status: {
                displayName: 'Status',
                displayNameForPossibleValue: (value) => {
                    return displayTextForChecklistItemStatusType(value as ChecklistItemStatusType);
                },
                defaultValues: Object.values(ChecklistItemStatusType),
                extractPossibleValueOrValuesFromItem: (item) => {
                    return item.status?.status ?? 'Not started';
                },
                showAsDropdown: true,
            },
        };
        return options;
    }, [displayNameForGroupId]);

    const updateFilteredItems = React.useCallback(
        (items: ChecklistItem[]) => {
            const checklistItemsByStepRunId = items.reduce<Record<string, ChecklistItem[]>>((list, item) => {
                const stepRunId = item.stepRunId ?? '';
                if (!list[stepRunId]) {
                    list[stepRunId] = [];
                }
                list[stepRunId].push(item);
                return list;
            }, {});

            setFilteredItems(
                props.stepRuns.map((stepRun) => ({
                    stepRun,
                    filteredChecklistItems: checklistItemsByStepRunId[stepRun.id] ?? [],
                })),
            );
        },
        [props.stepRuns],
    );

    return (
        <Layout style={{ paddingBottom: 95 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Input.Search
                    placeholder="Search"
                    style={{ maxWidth: 280 }}
                    allowClear
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                />
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FilterTableButton<ChecklistItem>
                        id={ChecklistItemPlacement.StepChecklist}
                        items={searchFilteredItems}
                        filterableKeysAndOptions={filterableKeysAndOptions}
                        onChange={updateFilteredItems}
                    />
                    <span style={{ marginLeft: 15 }}>{renderPingPendingButton(stepRunIds, stepArchetype)}</span>
                    {renderMarkAllAsCompletedButton(stepRunIds)}
                </div>
            </div>
            <Divider dashed style={{ marginBottom: 25 }} />
            <div>
                {Array.from(filteredItems.entries()).map(([_, item]) => {
                    const completedCount = item.filteredChecklistItems.filter(
                        (item) => item.status?.status === ChecklistItemStatusType.Approved,
                    ).length;
                    const totalCount = item.filteredChecklistItems.length;
                    const allCompleted = completedCount === totalCount;
                    const completedItemsString = `${completedCount} of ${totalCount}`;
                    const stepRunComponentProps: StepRunComponentProps = {
                        stepRunId: item.stepRun.id,
                        stepConfigId: item.stepRun.stepId,
                        workflowId: workflowId,
                        workflowRunId: workflowRunId,
                        orgId: orgId,
                        releaseId: releaseId,
                        appId: appId,
                    };
                    return (
                        <div style={{ marginBottom: 40 }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Space>
                                    {item.stepRun.destinations &&
                                        item.stepRun.destinations.map((destination) => (
                                            <DestinationCounter destinations={[destination]} />
                                        ))}
                                </Space>
                                <Divider type="vertical" style={{ height: '32px' }}></Divider>
                                <Text
                                    style={{
                                        fontSize: 15,
                                        fontWeight: 400,
                                        color: antGrey.grey8,
                                    }}
                                >
                                    {displayTextForReleaseStepType(item.stepRun.type, platform, appStoreType, true)}
                                </Text>
                                <Divider type="vertical" style={{ height: '32px' }}></Divider>
                                <Text
                                    style={{
                                        fontSize: 15,
                                        fontWeight: 400,
                                        color: antGrey.grey8,
                                    }}
                                >
                                    {item.stepRun.displayName}
                                </Text>
                                <Divider type="vertical" style={{ height: '32px' }}></Divider>
                                <div>
                                    <Text
                                        style={{
                                            fontSize: 15,
                                            fontWeight: 400,
                                            color: allCompleted ? runwayColors.statusGreen : runwayColors.statusYellow,
                                        }}
                                    >
                                        {completedItemsString}
                                    </Text>

                                    {allCompleted && (
                                        <CheckCircleFilled style={{ color: runwayColors.statusGreen, marginLeft: 4 }} />
                                    )}
                                </div>
                                <div style={{ marginLeft: 'auto' }}>
                                    {renderPingPendingButton([item.stepRun.id], item.stepRun.type)}
                                    {renderMarkAllAsCompletedButton([item.stepRun.id])}
                                    <Divider type="vertical" style={{ height: '32px' }}></Divider>
                                    <Link
                                        to={createStepRunRoute(
                                            params.orgIdParam,
                                            params.appIdParam,
                                            params.releaseIdParam,
                                            item.stepRun.id,
                                        )}
                                        style={{ alignSelf: 'flex-end' }}
                                    >
                                        <Button
                                            type="link"
                                            style={{
                                                fontSize: 13,
                                                fontWeight: 200,
                                                padding: 0,
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: 'inline-flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    gap: 8,
                                                    height: '100%',
                                                }}
                                            >
                                                <ArrowRightOutlined />
                                                View in step
                                            </div>
                                        </Button>
                                    </Link>
                                </div>
                            </div>
                            <Divider
                                type="horizontal"
                                style={{ marginTop: 13, marginBottom: 0, borderWidth: 2 }}
                            ></Divider>
                            <StepChecklistItems
                                orgId={orgId}
                                releaseId={releaseId}
                                appId={appId}
                                releaseStepType={releaseStepType}
                                items={item.filteredChecklistItems}
                                isEditing={false}
                                onEditChecklistItem={null}
                                onAddComment={null}
                                placement={ChecklistItemPlacement.StepChecklist}
                                onUpdateOrder={null}
                                stepRunComponentProps={stepRunComponentProps}
                            />
                        </div>
                    );
                })}
            </div>
        </Layout>
    );
};

export default StepGroupChecklistContainer;
